import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

const MyApplicationDetails = () => {
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { app_id } = useParams();
  const navigate = useNavigate();

  // Mock data for demonstration - replace with actual API call
  const applicationId = "123";
  const token = "mock-token";

  useEffect(() => {
    // Mock data for demonstration
    const mockApplication = {
      id: 123,
      job: {
        id: 456,
        title: "Senior Frontend Developer",
        company: "Tech Innovation Corp",
        location: "San Francisco, CA",
        type: "Full-time"
      },
      username: "john_doe",
      user: 789,
      status: "pending",
      date_updated: "2025-01-15T10:30:00Z",
      ai_rating: 8.5,
      ai_score: 85,
      ai_review: "Strong technical background with excellent React experience. The candidate demonstrates deep understanding of modern frontend development practices and has relevant experience with large-scale applications.",
      ai_advantages: "Extensive React experience, strong problem-solving skills, excellent communication abilities, and proven track record with complex projects.",
      ai_criticism: "Could benefit from more backend development experience and cloud platform knowledge. Portfolio could showcase more diverse project types.",
      ai_recommendation: "Consider highlighting any backend or full-stack experience. Prepare to discuss system architecture and scalability challenges you've solved.",
      cv: [
        {
          id: 1,
          original_filename: "John_Doe_Resume_2025.pdf",
          file_type: "application/pdf"
        }
      ],
      cover_letter: [
        {
          id: 2,
          original_filename: "Cover_Letter_TechCorp.pdf", 
          file_type: "application/pdf"
        }
      ],
      extra_form_file: [
        {
          id: 3,
          original_filename: "Portfolio_Screenshots.zip",
          file_type: "application/zip"
        }
      ],
      form_answers: {
        "Why do you want to work at Tech Innovation Corp?": "I'm passionate about building user-centric applications and your company's focus on innovation aligns perfectly with my career goals.",
        "What interests you most about this role?": "The opportunity to work with cutting-edge technologies and contribute to products that impact millions of users.",
        "Describe your experience with React": "I have 5+ years of React experience, including hooks, context API, and state management with Redux and Zustand."
      }
    };

    setTimeout(() => {
      setApplication(mockApplication);
      setLoading(false);
    }, 1000);
  }, [applicationId]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'accepted':
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'reviewing':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getRatingColor = (rating) => {
    if (!rating) return 'text-gray-400';
    if (rating >= 8) return 'text-green-600';
    if (rating >= 6) return 'text-yellow-600';
    if (rating >= 4) return 'text-orange-600';
    return 'text-red-600';
  };

  const downloadFile = (fileId, filename) => {
    // Mock download functionality
    console.log(`Downloading file: ${filename} (ID: ${fileId})`);
    alert(`Would download: ${filename}`);
  };

  if (loading) {
    return (
      <div className="lg:p-8">
        <div className="flex items-center justify-center min-h-64">
          <div className="text-gray-500">Loading application details...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lg:p-8">
        <div className="text-center">
          <div className="text-red-500 mb-4">{error}</div>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="lg:p-8">
        <div className="text-center">
          <div className="text-gray-500 mb-4">Application not found</div>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="lg:p-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => window.history.back()}
          className="text-blue-600 hover:text-blue-700 mb-4 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Applications
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">Application Details</h1>
        <p className="mt-1 text-sm text-gray-600">
          Complete overview of your job application
        </p>
      </div>

      <div className="space-y-6">
        {/* Job Information */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Job Information</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">
                {application.job?.title}
              </h3>
              <p className="text-gray-600 mb-1">
                <span className="font-medium">Company:</span> {application.job?.company}
              </p>
              <p className="text-gray-600 mb-1">
                <span className="font-medium">Location:</span> {application.job?.location}
              </p>
              <p className="text-gray-600">
                <span className="font-medium">Type:</span> {application.job?.type}
              </p>
            </div>
            <div>
              <div className="mb-4">
                <span className="text-sm font-medium text-gray-500">Application Status</span>
                <div className={`inline-block mt-1 px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(application.status)}`}>
                  {application.status || 'Pending'}
                </div>
              </div>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Applied on:</span> {' '}
                {new Date(application.date_updated).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
          </div>
        </div>

        {/* AI Analysis */}
        {(application.ai_review || application.ai_rating || application.ai_score) && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">AI Analysis</h2>
            
            {(application.ai_rating || application.ai_score) && (
              <div className="mb-6 grid md:grid-cols-2 gap-4">
                {application.ai_rating && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-600 mb-1">AI Rating</h4>
                    <div className={`text-2xl font-bold ${getRatingColor(application.ai_rating)}`}>
                      {application.ai_rating}/10
                    </div>
                  </div>
                )}
                {application.ai_score && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-600 mb-1">AI Score</h4>
                    <div className={`text-2xl font-bold ${getRatingColor(application.ai_score)}`}>
                      {application.ai_score}%
                    </div>
                  </div>
                )}
              </div>
            )}

            {application.ai_review && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-600 mb-2">AI Review</h4>
                <div className="bg-blue-50 rounded-lg p-4 text-sm text-gray-700">
                  {application.ai_review}
                </div>
              </div>
            )}

            {application.ai_advantages && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-600 mb-2">Advantages</h4>
                <div className="bg-green-50 rounded-lg p-4 text-sm text-gray-700">
                  {application.ai_advantages}
                </div>
              </div>
            )}

            {application.ai_criticism && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-600 mb-2">Areas for Improvement</h4>
                <div className="bg-yellow-50 rounded-lg p-4 text-sm text-gray-700">
                  {application.ai_criticism}
                </div>
              </div>
            )}

            {application.ai_recommendation && (
              <div>
                <h4 className="text-sm font-medium text-gray-600 mb-2">Recommendations</h4>
                <div className="bg-purple-50 rounded-lg p-4 text-sm text-gray-700">
                  {application.ai_recommendation}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Application Files */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Application Files</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* CV Files */}
            {application.cv && application.cv.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-600 mb-3">CV/Resume</h4>
                <div className="space-y-2">
                  {application.cv.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{file.original_filename}</p>
                          <p className="text-xs text-gray-500">{file.file_type}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => downloadFile(file.id, file.original_filename)}
                        className="text-blue-600 hover:text-blue-700 text-sm"
                      >
                        Download
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Cover Letter Files */}
            {application.cover_letter && application.cover_letter.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-600 mb-3">Cover Letter</h4>
                <div className="space-y-2">
                  {application.cover_letter.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{file.original_filename}</p>
                          <p className="text-xs text-gray-500">{file.file_type}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => downloadFile(file.id, file.original_filename)}
                        className="text-blue-600 hover:text-blue-700 text-sm"
                      >
                        Download
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Extra Files */}
            {application.extra_form_file && application.extra_form_file.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-600 mb-3">Additional Files</h4>
                <div className="space-y-2">
                  {application.extra_form_file.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{file.original_filename}</p>
                          <p className="text-xs text-gray-500">{file.file_type}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => downloadFile(file.id, file.original_filename)}
                        className="text-blue-600 hover:text-blue-700 text-sm"
                      >
                        Download
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {!application.cv?.length && !application.cover_letter?.length && !application.extra_form_file?.length && (
            <div className="text-center py-8">
              <div className="text-gray-400 text-4xl mb-2">📎</div>
              <p className="text-gray-500">No files uploaded with this application</p>
            </div>
          )}
        </div>

        {/* Form Answers */}
        {application.form_answers && Object.keys(application.form_answers).length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Application Form Answers</h2>
            <div className="space-y-4">
              {Object.entries(application.form_answers).map(([question, answer], index) => (
                <div key={index} className="border-l-4 border-blue-500 pl-4 py-2">
                  <h4 className="text-sm font-medium text-gray-700 mb-1">{question}</h4>
                  <p className="text-sm text-gray-600">{answer}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Application Meta */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Application Information</h2>
          <div className="grid md:grid-cols-2 gap-6 text-sm">
            <div>
              <p className="text-gray-600 mb-2">
                <span className="font-medium">Application ID:</span> #{application.id}
              </p>
              <p className="text-gray-600 mb-2">
                <span className="font-medium">Username:</span> {application.username}
              </p>
              <p className="text-gray-600">
                <span className="font-medium">User ID:</span> {application.user}
              </p>
            </div>
            <div>
              <p className="text-gray-600 mb-2">
                <span className="font-medium">Job ID:</span> {application.job?.id}
              </p>
              <p className="text-gray-600">
                <span className="font-medium">Last Updated:</span> {' '}
                {new Date(application.date_updated).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyApplicationDetails;